import React from 'react';
import {push} from 'connected-react-router/immutable';
import {useLingui} from '@lingui/react';
import {Trans} from '@lingui/macro';
import {useDispatch, useSelector} from 'react-redux';
import Immutable, {fromJS} from 'immutable';
import parse from 'date-fns/parse';
import isAfter from 'date-fns/isAfter';
import closestTo from 'date-fns/closestTo';
import cl from 'classnames';

import CalendarSidebar from './CalendarSidebar';
import FlatButton from '@components/FlatButton';
import Dialog from '@components/Dialog';
import InfoIcon from '@components/Icons/InfoIcon';
import EmailPreview from './EmailPreview';
import ChatPreview from './ChatPreview';
import CalendarTutorial from './CalendarTutorial';
import CompleteTestModal from './CompleteTestModal';
import Calendar from './Calendar';

import {setCurrentTest} from '@actions/tests';
import {formatDate} from '@services/utilities';
import {loadTests} from '@actions/tests';
import {getTests} from '@reducers/tests';
import {TEST} from '@services/routes';
import {setCalendarCurrentPosition, setHasSeenCalendarTutorial} from '@actions/userCampaign';

import s from './CalendarTheme.module.css';

// const calendarStore = {
//   currentPosition: '01/11/2021',
//   hasSeenTutorial: true,
//   tests: {'05/11/2021': {testId: 1}, '10/11/2021': {testId: 5}, '08/11/2021': {testId: 10}},
// };

//formatDate(new Date('November 1, 2021'), 'dd/MM/yyyy')

export default function CalendarTheme({showCompleteTestModal, closeCompleteTestModal}) {
  const {i18n} = useLingui();
  const dispatch = useDispatch();

  const tests = useSelector(getTests);
  const lang = useSelector(state => state.getIn(['userCampaign', 'data', 'user', 'lang']));
  const campaignThemeData = useSelector(state => {
    return state.getIn(['userCampaign', 'data', 'data']);
  });

  const calendarStorage = useSelector(state => {
    const testsIds = tests.map(test => test.get('id'));
    const storage = state.getIn(['userCampaign', 'data', 'storage']);
    //check if there's any test that are in the storage but not in testList
    if (!storage) {
      return campaignThemeData.withMutations(map => {
        const testsData = map.get('tests');
        testsData.keySeq().forEach(date => {
          const dateTestId = testsData.getIn([date, 'testId']);
          if (!testsIds.includes(dateTestId)) {
            map.deleteIn(['tests', date]);
          }
        });
      });
    }

    return storage.withMutations(map => {
      const testsData = map.get('tests');
      testsData.keySeq().forEach(date => {
        const dateTestId = testsData.getIn([date, 'testId']);
        if (!testsIds.includes(dateTestId)) {
          map.deleteIn(['tests', date]);
        }
      });
    });
  });

  const [openPreviewTest, setOpenPreviewTest] = React.useState(null);
  const [rewatchTutorial, setRewatchTutorial] = React.useState(false);
  const [selectedTest, setSelectedTest] = React.useState(null);

  React.useEffect(() => {
    dispatch(loadTests());
  }, []);

  function handleSelectTest(testId) {
    if (!testId) return;

    const calendarTests = calendarStorage.get('tests');
    const areThereTestsOnCurrentDay = Boolean(calendarTests.get(currentPosition));
    const currentTest = areThereTestsOnCurrentDay
      ? testsWithCalendarDate.find(
          test => test.get('id') === calendarTests.getIn([currentPosition, 'testId'])
        )
      : null;

    if (testId === currentTest?.get('id') && currentTest?.getIn(['status']) === 'complete') {
      return;
    }

    setSelectedTest(selectedTest === testId ? null : testId);
  }

  function goToNextTest() {
    const calendarTests = calendarStorage.get('tests');
    const currentPosition = calendarStorage.get('currentPosition');

    const parsedCurrentPosition = parse(currentPosition, 'dd/MM/yyyy', new Date());
    const parsedCalendarTestDates = calendarTests
      .keySeq()
      .map(date => parse(date, 'dd/MM/yyyy', new Date()));

    const datesGreaterThanCurrentPosition = parsedCalendarTestDates.filter(date => {
      return isAfter(date, parsedCurrentPosition);
    });

    const nextActivityDate = closestTo(parsedCurrentPosition, datesGreaterThanCurrentPosition);
    const formattedNextActivityDate = formatDate(nextActivityDate, 'dd/MM/yyyy');
    dispatch(setCalendarCurrentPosition(formattedNextActivityDate));
  }

  function onFooterClick() {
    const calendarTests = calendarStorage.get('tests');
    const currentPosition = calendarStorage.get('currentPosition');

    const areThereTestsOnCurrentDay = Boolean(calendarTests.get(currentPosition));
    const currentTest = areThereTestsOnCurrentDay
      ? testsWithCalendarDate.find(
          test => test.get('id') === calendarTests.getIn([currentPosition, 'testId'])
        )
      : null;

    if (areThereTestsOnCurrentDay && currentTest.get('status') !== 'complete') {
      const testId = calendarTests.getIn([currentPosition, 'testId']);
      setOpenPreviewTest(testId);
      return;
    }

    goToNextTest();
  }

  function startTest() {
    closeDialog();

    const calendarTests = calendarStorage.get('tests');
    const currentPosition = calendarStorage.get('currentPosition');

    const todaysTestId = calendarTests.getIn([currentPosition, 'testId']);
    const todaysTest = tests.find(test => test.get('id') === todaysTestId);
    dispatch(setCurrentTest(todaysTest));
    dispatch(push(TEST));
  }

  function closeDialog() {
    setOpenPreviewTest(null);
  }

  function closeVideoDialog() {
    setRewatchTutorial(false);
    dispatch(setHasSeenCalendarTutorial());
  }

  const calendarTests = calendarStorage.get('tests');
  const currentPosition = calendarStorage.get('currentPosition');
  const hasSeenTutorial = calendarStorage.get('hasSeenTutorial');

  const currentDate = parse(currentPosition, 'dd/MM/yyyy', new Date());
  const today = formatDate(currentDate, 'EEEE d', lang);
  const areThereTestsOnCurrentDay = Boolean(calendarTests.get(currentPosition));

  const testsWithCalendarDate = tests?.map(test => {
    const calendarTests = calendarStorage.get('tests');
    const foundCalendarTestDate = calendarTests
      .keySeq()
      .find(date => calendarTests.getIn([date, 'testId']) === test.get('id'));
    const foundCalendarTest = calendarTests.get(foundCalendarTestDate);

    if (foundCalendarTest) {
      return test.set('startDate', foundCalendarTestDate);
    }

    return test;
  });

  const currentTest = areThereTestsOnCurrentDay
    ? testsWithCalendarDate.find(
        test => test.get('id') === calendarTests.getIn([currentPosition, 'testId'])
      )
    : null;
  const fullSelectedTest = selectedTest
    ? testsWithCalendarDate.find(test => test.get('id') === selectedTest)
    : null;
  const canOpenSelectedTest = selectedTest
    ? fullSelectedTest.get('startDate') === currentPosition
    : Boolean(currentTest) && currentTest?.get('status') !== 'complete';

  const selectedTestToShow = selectedTest
    ? selectedTest
    : areThereTestsOnCurrentDay
    ? calendarTests.getIn([currentPosition, 'testId'])
    : null;
  const isEmail = currentTest?.getIn(['jsonData', 'theme', 'preview', 'type']) === 'email';

  const hasCompletedAllTests =
    tests.size > 0 ? tests.every(test => test.get('status') === 'complete') : false;

  return (
    <>
      <div className={s.CalendarThemeContainer}>
        <CalendarSidebar
          position={currentPosition}
          tests={testsWithCalendarDate}
          selectedTest={selectedTestToShow}
          calendarTests={calendarTests}
          openPreviewTest={onFooterClick}
        />

        <div className={s.CalendarSection}>
          <div className={s.SectionHeader}>
            <div className={cl(s.Title, 'center-y')}>
              Agenda{' '}
              <FlatButton
                id="rewatch-tutorial-btn"
                onClick={() => setRewatchTutorial(true)}
                className={s.TutIcon}
                title={i18n._('calendar.start_tut')}
              >
                <InfoIcon />
              </FlatButton>
            </div>

            <div id="sk-calendar-today" className={s.Date}>
              {today}
            </div>
          </div>

          <Calendar
            position={currentPosition}
            tests={testsWithCalendarDate}
            selectedTest={selectedTestToShow}
            onSelectTest={handleSelectTest}
          />

          <footer
            id="sk-calendar-footer"
            className={cl(s.Footer, !canOpenSelectedTest && selectedTest ? s.Disabled : '')}
          >
            <FlatButton onClick={onFooterClick} disabled={!canOpenSelectedTest && selectedTest}>
              {(areThereTestsOnCurrentDay && currentTest?.get('status') !== 'complete') ||
              selectedTest ? (
                <Trans id="calendar.start_activity">Inizia l'attività</Trans>
              ) : (
                <Trans id="calendar.next_activity">'Vai alla prossima attività'</Trans>
              )}
            </FlatButton>
          </footer>
        </div>
      </div>

      <Dialog className={s.PreviewDialog} isOpen={openPreviewTest !== null}>
        {isEmail ? (
          <EmailPreview test={currentTest} startTest={startTest} close={closeDialog} />
        ) : (
          <ChatPreview test={currentTest} startTest={startTest} close={closeDialog} />
        )}
      </Dialog>

      {!hasSeenTutorial || rewatchTutorial ? (
        <CalendarTutorial selectedTest={selectedTest} isOpen={true} close={closeVideoDialog} />
      ) : null}

      <Dialog
        isOpen={showCompleteTestModal || hasCompletedAllTests}
        className={cl(s.PreviewDialog, s.CompleteDialog)}
      >
        <CompleteTestModal
          hasCompletedAllTests={hasCompletedAllTests}
          close={closeCompleteTestModal}
        />
      </Dialog>
    </>
  );
}
